import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { formatLongDatetime } from "@/helpers/dates";
import { documentNumber } from "@/helpers/document-number";
import { formatPrice } from "@/helpers/price";
import { useCurrentStore, usePaginatedSearch, useSales } from "../../hooks";

const Sales = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("sales.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { page, setPage } = usePaginatedSearch();

	// The seller search used to be `q[userFirstnameOrUserLastnameCont]`, which
	// traverses the `user` association. That association is no longer ransackable
	// (it was a path to the users table and its auth columns), so the API now
	// silently ignores the predicate and returns every sale. Sending it would
	// render a search box that looks like it filters but never does, so the box is
	// hidden until a supported way to filter by seller exists.
	const { data, isLoading } = useSales({
		page,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": "created_at desc",
	});

	const columnHelper = createColumnHelper<DataTableFeatures, Sale>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor("number", {
					header: t("sales.table.id"),
					cell: (props) => (
						<Link className="underline" to={`/sales/${props.row.original.id}`}>
							{documentNumber(props.getValue())}
						</Link>
					),
				}),
				columnHelper.accessor("totalAmountCents", {
					header: t("sales.table.amount"),
					cell: (props) => (
						<span>
							{formatPrice(
								props.getValue(),
								props.row.original.totalAmountCurrency,
							)}
						</span>
					),
				}),
				columnHelper.accessor(
					(row) => `${row.user.firstname} ${row.user.lastname}`,
					{
						id: "fullname",
						header: t("sales.table.user"),
						cell: (props) => (
							<Link
								className="underline"
								to={`/users/${props.row.original.user.id}`}
							>
								{props.getValue()}
							</Link>
						),
					},
				),
				columnHelper.accessor("createdAt", {
					header: t("sales.table.createdAt"),
					cell: (props) => formatLongDatetime(props.getValue()),
				}),
			] as ColumnDef<DataTableFeatures, Sale>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("sales.searchPlaceholder")}
			isLoading={isLoading}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default Sales;
